'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import React, { useState } from 'react'
import { cn } from '../lib/utils'
import { NavbarItem } from './Navbar'

function OpenMenuIcon() {
    return (
        <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
        </svg>
    )
}

function CloseMenuIcon() {
    return (
        <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
        </svg>
    )
}

function MobileSidebar({
    open,
    close,
    topOffset,
    children,
}: React.PropsWithChildren<{ open: boolean; close: () => void; topOffset: boolean }>) {
    return (
        <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && close()}>
            <DialogPrimitive.Portal>
                <div className="lg:hidden">
                    <DialogPrimitive.Overlay
                        className={cn(
                            'fixed inset-0 z-50 bg-black/30 transition',
                            topOffset && 'top-10',
                            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0'
                        )}
                    />
                    <DialogPrimitive.Content
                        className={cn(
                            'fixed inset-y-0 left-0 z-50 w-full max-w-80 p-2 transition duration-300 ease-in-out',
                            topOffset && 'top-10',
                            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:animate-in data-[state=open]:slide-in-from-left'
                        )}
                    >
                        <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
                        <div className="flex h-full flex-col rounded-3xl bg-card shadow-[0_8px_24px_-4px_rgb(25_28_29_/_0.12),0_2px_8px_-2px_rgb(25_28_29_/_0.08)]">
                            <div className="-mb-3 px-4 pt-3">
                                <DialogPrimitive.Close asChild>
                                    <NavbarItem aria-label="Close navigation">
                                        <CloseMenuIcon />
                                    </NavbarItem>
                                </DialogPrimitive.Close>
                            </div>
                            {children}
                        </div>
                    </DialogPrimitive.Content>
                </div>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    )
}

export function SidebarLayout({
    navbar,
    sidebar,
    topBanner,
    header,
    children,
}: React.PropsWithChildren<{
    navbar: React.ReactNode
    sidebar: React.ReactNode
    topBanner?: React.ReactNode
    /** Persistent header shown above the page content on every breakpoint (unlike `navbar`, which is mobile-only). */
    header?: React.ReactNode
}>) {
    let [showSidebar, setShowSidebar] = useState(false)
    let hasTopBanner = Boolean(topBanner)

    return (
        <div
            className={cn(
                'relative isolate flex min-h-svh w-full max-lg:flex-col',
                hasTopBanner && 'pt-10'
            )}
        >
            {/* Sub top header: shown above the sidebar and content when a new build is available */}
            {topBanner && <div className="fixed inset-x-0 top-0 z-50 h-10">{topBanner}</div>}

            {/* Sidebar on desktop — a floating card inset from the viewport edge, not a flush rail */}
            <div
                className={cn(
                    'fixed left-5 w-64 rounded-3xl bg-card shadow-sm max-lg:hidden',
                    hasTopBanner ? 'top-[calc(2.5rem+1.25rem)] bottom-5' : 'top-5 bottom-5'
                )}
            >
                {sidebar}
            </div>

            {/* Sidebar on mobile */}
            <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)} topOffset={hasTopBanner}>
                {sidebar}
            </MobileSidebar>

            {/* Navbar on mobile */}
            <header className="flex items-center px-4 lg:hidden">
                <div className="py-2.5">
                    <NavbarItem onClick={() => setShowSidebar(true)} aria-label="Open navigation">
                        <OpenMenuIcon />
                    </NavbarItem>
                </div>
                <div className="min-w-0 flex-1">{navbar}</div>
            </header>

            {/* Content */}
            <main className="flex flex-1 flex-col pb-5 pt-5 pr-5 lg:min-w-0 lg:pl-[calc(16rem+2.5rem)]">
                <div className="flex grow flex-col px-4 lg:px-2">
                    {header && (
                        <div className="sticky top-5 z-20 mb-4 rounded-full bg-card px-4 py-2.5 shadow-sm lg:px-6">
                            <div className="flex items-center gap-4">{header}</div>
                        </div>
                    )}
                    <div className="grow">
                        {children}
                    </div>
                </div>
            </main>
        </div>
    )
}
